import { useState } from 'react';

import { DropdownMenu } from '../DropdownMenu';
import { Icon, type IconName } from '../Icon';
import styles from './ModuleSidebar.module.css';

export interface ModuleSidebarItem {
  readonly href?: string;
  readonly label: string;
}

/**
 * 带标题的分组。省略 `title` 时按「平铺分组」处理（默认外观的写法）。
 * `title` + `href` 且没有 `items` 时是叶子入口（后台「概览」这类单行菜单）。
 */
export interface ModuleSidebarSection {
  readonly href?: string;
  readonly icon?: IconName;
  readonly id?: string;
  readonly items?: readonly ModuleSidebarItem[];
  readonly title?: string;
}

export type ModuleSidebarGroup = readonly ModuleSidebarItem[] | ModuleSidebarSection;

export interface ModuleSidebarProps {
  activeItem: string;
  /** `admin` 为管理后台的分组折叠外观：分组标题行（可带图标）、子项缩进、无圆点与分隔线。 */
  appearance?: 'admin' | 'default';
  ariaLabel: string;
  /**
   * 后台外观分组的展开指示图形：`thin`（默认）是细箭头 `a-arrow-up` / `a-arrow-down`，
   * `solid` 是实心小三角 `caret-top` / `caret-bottom`（后台第二列的二级菜单用这个）。
   */
  caret?: 'solid' | 'thin';
  /**
   * 后台外观分组的初始展开策略：`active`（默认）只展开当前项所在的分组，`all` 全展开。
   * 两者都保留手动开合，用户操作后以用户的选择为准。
   */
  defaultExpandedGroups?: 'active' | 'all';
  groups: readonly ModuleSidebarGroup[];
  onItemSelect?: (item: ModuleSidebarItem) => void;
  /** 是否显示底部的展开／收起控制，默认显示。 */
  showCollapseControl?: boolean;
  /** 白色底分栏（后台第二列这类），默认沿用模块侧栏的中性底色。 */
  surface?: boolean;
  /** 顶部标题（如后台第二列当前功能的名称）。 */
  title?: string;
}

interface ResolvedSection {
  readonly href?: string;
  readonly icon?: IconName;
  readonly id?: string;
  readonly items: readonly ModuleSidebarItem[];
  readonly title?: string;
}

function isItemList(group: ModuleSidebarGroup): group is readonly ModuleSidebarItem[] {
  return Array.isArray(group);
}

function resolveSection(group: ModuleSidebarGroup): ResolvedSection {
  return isItemList(group) ? { items: group } : { ...group, items: group.items ?? [] };
}

function sectionKey(section: ResolvedSection, index: number): string {
  const fallback = section.items.map((item) => item.label).join('-');
  return section.id ?? section.title ?? (fallback || `section-${index}`);
}

interface RowProps {
  active: boolean;
  item: ModuleSidebarItem;
  onItemSelect?: (item: ModuleSidebarItem) => void;
}

function SidebarRow({ active, item, onItemSelect }: RowProps) {
  const className = `${styles.item}${active ? ` ${styles.active}` : ''}`;
  if (item.href) {
    return (
      <a
        aria-current={active ? 'page' : undefined}
        className={className}
        href={item.href}
        onClick={() => onItemSelect?.(item)}
      >
        {item.label}
      </a>
    );
  }
  return (
    <button
      aria-current={active ? 'page' : undefined}
      className={className}
      type="button"
      onClick={() => onItemSelect?.(item)}
    >
      {item.label}
    </button>
  );
}

/** 后台外观的一个分组：标题行 + 缩进子项，收起后只留图标并改用浮层。 */
function AdminSection({ activeItem, caret = 'thin', collapsed, expandAll, onItemSelect, section }: {
  activeItem: string;
  caret?: 'solid' | 'thin';
  collapsed: boolean;
  expandAll: boolean;
  onItemSelect?: (item: ModuleSidebarItem) => void;
  section: ResolvedSection;
}) {
  const key = section.id ?? section.title ?? section.items[0]?.label ?? 'section';
  const leaf = section.items.length === 0;
  const holdsActive = section.items.some((item) => item.label === activeItem);
  /* `active` 策略下分组默认收起、只有当前项所在的分组展开（见 defaultExpandedGroups）；
     用户手动开合后以用户的选择为准。 */
  const [userExpanded, setUserExpanded] = useState<boolean | null>(null);
  const expanded = userExpanded ?? (expandAll || holdsActive);
  /* 有下级的分组不跟随子项进入选中态；整栏收起后分组图标是唯一的位置提示，此时保留。 */
  const active = leaf ? section.title === activeItem : collapsed && holdsActive;
  const icon = section.icon ? <Icon className={styles.groupIcon} name={section.icon} size="base" /> : null;

  if (!section.title) {
    return (
      <div className={styles.group}>
        {section.items.map((item) => (
          <SidebarRow active={item.label === activeItem} item={item} key={item.label} onItemSelect={onItemSelect} />
        ))}
      </div>
    );
  }

  /* 叶子入口（概览这类）：单行链接，收起时只留图标。 */
  if (leaf) {
    return (
      <a
        aria-current={active ? 'page' : undefined}
        aria-label={collapsed ? section.title : undefined}
        className={styles.groupTrigger}
        data-active={active || undefined}
        href={section.href}
        title={section.title}
      >
        {collapsed ? (section.icon ? icon : <span aria-hidden="true">{section.title.slice(0, 1)}</span>) : icon}
        {collapsed ? null : <span className={styles.label}>{section.title}</span>}
      </a>
    );
  }

  if (collapsed) {
    return (
      <DropdownMenu
        ariaLabel={`${section.title}菜单`}
        arrow={false}
        items={section.items.map((item) => ({ href: item.href, id: item.label, label: item.label }))}
        onItemSelect={() => undefined}
        placement="rightTop"
        trigger={(
          <button className={styles.groupTrigger} data-active={active || undefined} title={section.title} type="button">
            {section.icon ? icon : <span aria-hidden="true">{section.title.slice(0, 1)}</span>}
          </button>
        )}
        triggerMode="hover"
      />
    );
  }

  const panelId = `module-sidebar-${key}-items`;
  const disclosure = (
    <Icon
      className={caret === 'thin' ? `${styles.caret} ${styles.trailingCaret}` : styles.caret}
      name={caret === 'solid'
        ? (expanded ? 'caret-top' : 'caret-bottom')
        : (expanded ? 'a-arrow-up' : 'a-arrow-down')}
      size={caret === 'solid' ? 'xs' : 'sm'}
    />
  );
  return (
    <div className={styles.group}>
      <button
        aria-controls={expanded ? panelId : undefined}
        aria-expanded={expanded}
        className={styles.groupTrigger}
        data-active={active || undefined}
        type="button"
        onClick={() => setUserExpanded(!expanded)}
      >
        {caret === 'solid' ? disclosure : null}
        {icon}
        <span className={styles.label}>{section.title}</span>
        {caret === 'thin' ? disclosure : null}
      </button>
      {expanded ? (
        <div id={panelId}>
          {section.items.map((item) => (
            <SidebarRow active={item.label === activeItem} item={item} key={item.label} onItemSelect={onItemSelect} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function ModuleSidebar({ activeItem, appearance = 'default', ariaLabel, caret = 'thin', defaultExpandedGroups = 'active', groups, onItemSelect, showCollapseControl = true, surface = false, title }: ModuleSidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const sections = groups.map(resolveSection);

  return (
    <aside className={styles.root} data-appearance={appearance} data-collapsed={collapsed || undefined} data-surface={surface || undefined} aria-label={ariaLabel}>
      {title ? <div className={styles.title}>{title}</div> : null}
      <nav className={styles.navigation} aria-label={`${ariaLabel}功能`}>
        {appearance === 'admin'
          ? sections.map((section, index) => (
            <AdminSection
              activeItem={activeItem}
              caret={caret}
              collapsed={collapsed}
              expandAll={defaultExpandedGroups === 'all'}
              key={sectionKey(section, index)}
              onItemSelect={onItemSelect}
              section={section}
            />
          ))
          : sections.map((section, index) => (
            <div className={styles.group} key={sectionKey(section, index)}>
              {section.items.map((item) => {
                const active = item.label === activeItem;
                const className = `${styles.item} ${active ? styles.active : ''}`;

                return item.href ? (
                  <a
                    aria-current={active ? 'page' : undefined}
                    aria-label={collapsed ? item.label : undefined}
                    className={className}
                    href={item.href}
                    key={item.label}
                    onClick={() => onItemSelect?.(item)}
                  >
                    <span aria-hidden="true" className={styles.marker} />
                    <span className={styles.label}>{item.label}</span>
                  </a>
                ) : (
                  <button
                    aria-current={active ? 'page' : undefined}
                    aria-label={collapsed ? item.label : undefined}
                    className={className}
                    key={item.label}
                    type="button"
                    onClick={() => onItemSelect?.(item)}
                  >
                    <span aria-hidden="true" className={styles.marker} />
                    <span className={styles.label}>{item.label}</span>
                  </button>
                );
              })}
              {index < sections.length - 1 ? <div aria-hidden="true" className={styles.divider} role="separator" /> : null}
            </div>
          ))}
      </nav>
      {showCollapseControl ? (
        <button
          aria-expanded={!collapsed}
          aria-label={collapsed ? `展开${ariaLabel}` : `收起${ariaLabel}`}
          className={styles.collapse}
          type="button"
          onClick={() => setCollapsed((value) => !value)}
        >
          <Icon name={collapsed ? 'indent-increase' : 'indent-decrease'} size="sm" />
          {!collapsed ? <span className={styles.collapseText}>收起</span> : null}
        </button>
      ) : null}
    </aside>
  );
}
